import type { Salarie, SalaireCategorie } from '../types';

/** Correspondance libellé de catégorie dans le poste ou le nom (plus long d’abord). */
export function inferSalaireCategory(
  s: Salarie,
  categories: SalaireCategorie[],
): SalaireCategorie | null {
  if (categories.length === 0) return null;
  const hay = `${s.poste ?? ''} ${s.nom ?? ''}`.toLowerCase();
  const sorted = [...categories].sort((a, b) => b.label.length - a.label.length);
  for (const c of sorted) {
    const L = c.label.trim().toLowerCase();
    if (L.length < 2) continue;
    if (hay.includes(L)) return c;
  }
  return null;
}

export type SalarieGroupeResolved = {
  groupKey: string;
  groupLabel: string;
  /** Libellé affiché dans la colonne Catégorie */
  columnLabel: string | null;
  columnCategorieId: string | null;
  inferred: boolean;
};

export function resolveSalarieGroupe(
  s: Salarie,
  salaireCategories: SalaireCategorie[],
): SalarieGroupeResolved {
  const validIds = new Set(salaireCategories.map((c) => c.id));
  const byId = new Map(salaireCategories.map((c) => [c.id, c] as const));

  if (s.categorieId && validIds.has(s.categorieId)) {
    const cat = byId.get(s.categorieId)!;
    return {
      groupKey: s.categorieId,
      groupLabel: cat.label,
      columnLabel: cat.label,
      columnCategorieId: s.categorieId,
      inferred: false,
    };
  }

  const guessed = inferSalaireCategory(s, salaireCategories);
  if (guessed) {
    return {
      groupKey: guessed.id,
      groupLabel: guessed.label,
      columnLabel: guessed.label,
      columnCategorieId: guessed.id,
      inferred: true,
    };
  }

  return {
    groupKey: '_sans',
    groupLabel: 'Sans catégorie',
    columnLabel: null,
    columnCategorieId: null,
    inferred: false,
  };
}

/** Tri par période : mois d’entrée croissant, puis mois de sortie (toujours en activité en dernier), puis nom. */
export function sortSalariesByPeriode(a: Salarie, b: Salarie): number {
  if (a.moisEntree !== b.moisEntree) return a.moisEntree - b.moisEntree;
  const endA = a.moisSortie ?? Number.POSITIVE_INFINITY;
  const endB = b.moisSortie ?? Number.POSITIVE_INFINITY;
  if (endA !== endB) return endA - endB;
  return a.nom.localeCompare(b.nom, 'fr');
}

export function buildSalaireTableGroups(
  salaries: Salarie[],
  salaireCategories: SalaireCategorie[],
): { key: string; label: string; items: Salarie[] }[] {
  const validIds = new Set(salaireCategories.map((c) => c.id));
  const byCat = new Map<string, Salarie[]>();
  for (const c of salaireCategories) byCat.set(c.id, []);

  const uncategorized: Salarie[] = [];

  for (const s of salaries) {
    const r = resolveSalarieGroupe(s, salaireCategories);
    if (r.groupKey === '_sans') {
      uncategorized.push(s);
    } else if (validIds.has(r.groupKey)) {
      byCat.get(r.groupKey)!.push(s);
    } else {
      uncategorized.push(s);
    }
  }

  const out: { key: string; label: string; items: Salarie[] }[] = [];
  for (const c of salaireCategories) {
    const items = (byCat.get(c.id) ?? []).sort(sortSalariesByPeriode);
    out.push({ key: c.id, label: c.label, items });
  }
  if (uncategorized.length > 0) {
    uncategorized.sort(sortSalariesByPeriode);
    out.push({ key: '_sans', label: 'Sans catégorie', items: uncategorized });
  }
  return out;
}
